import { defineStore } from 'pinia'
import { ref } from 'vue'
import type { NotificationModel } from '@/types/notification.model'
import type { PaginatedResult } from '@/types/paginatedResult.model'
import {
  getUnreadNotificationCount,
  getNotifications,
  deleteNotification,
  markNotificationAsRead,
} from '@/services/notification.ts'

export const useNotificationStore = defineStore('notificationStore', () => {
  const results = ref<PaginatedResult<NotificationModel>>({
    items: [],
    total: 0,
    limit: 10,
    page: 1,
  })

  const unreadNotificationCount = ref<number>(0)
  const page = ref<number>(1)
  const pageSize = ref<number>(10)
  const loading = ref<boolean>(false)
  const error = ref<string | null>(null)

  async function update() {
    loading.value = true
    error.value = null

    const result = await getNotifications(page.value, pageSize.value)
    if (result.success) {
      results.value = result.data
    } else {
      error.value = 'Failed to fetch notifications'
    }
    loading.value = false
  }

  async function read(notificationId: string) {
    const result = await markNotificationAsRead(notificationId)
    if (result.success) {
      const notification = results.value.items.find((n: NotificationModel) => n.id === notificationId)
      if (notification) {
        notification.markedAsRead = true
      }
    } else {
      console.error('Failed to mark notification as read:', result.error)
    }
  }

  async function remove(notificationId: string) {
    const result = await deleteNotification(notificationId)
    if (result.success) {
      results.value.items = results.value.items.filter((n: NotificationModel) => n.id !== notificationId)
    } else {
      console.error('Failed to delete notification:', result.error)
    }
  }

  async function updateUnreadCounter() {
    loading.value = true
    const result = await getUnreadNotificationCount()
    if (result.success) {
      unreadNotificationCount.value = result.data.unreadCount
    } else {
      console.error('Failed to fetch notifications:', result.error)
    }
    loading.value = false
  }

  // Poll every 30 seconds
  setInterval(updateUnreadCounter, 30000)

  return {
    results,
    unreadNotificationCount,
    loading,
    page,
    pageSize,
    update,
    updateUnreadCounter,
    read,
    remove,
  }
})
